/**
 * Day Summary Card Component
 * Shows a rich summary for a selected trip day including weather, metrics, and next activity
 */

import React, { useMemo, useState } from 'react';
import { View, StyleSheet, Pressable, Platform, ScrollView } from 'react-native';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Body, Caption, ThemedText } from '@/components/ui/Text';
import { Button } from '@/components/ui/Button';
import { Ionicons } from '@expo/vector-icons';
import type { ItineraryItem } from '@/services/itinerary';
import type { TripDay } from './DaySelector';
import { calculateDistance } from '@/services/location';
import { toLocalDateKey } from '@/utils/dateKeys';

export type DayWeather = {
  condition: 'sunny' | 'cloudy' | 'rainy' | 'snowy' | 'partly_cloudy' | 'stormy' | 'windy';
  tempHigh: number;
  tempLow: number;
  tempCurrent?: number;
  icon?: string;
};

export type DaySummaryCardProps = {
  day: TripDay | null;
  weather?: DayWeather | null;
  weatherByDateKey?: Record<string, DayWeather>;
  weatherStatus?: 'idle' | 'loading' | 'ready' | 'error';
  weatherError?: string | null;
  onRetryWeather?: () => void;
  activities: ItineraryItem[];
  pins?: Array<{
    id: string;
    type?: 'itinerary' | 'landmark' | 'user' | 'search' | string;
    coordinates: { latitude: number; longitude: number };
  }>;
  onTapNextActivity?: (activity: ItineraryItem) => void;
  onTapAddActivity?: () => void;
  onTapViewAll?: () => void;
  totalDays?: number;
  compact?: boolean;
  footer?: React.ReactNode;
  preferredTravelMode?: 'walking' | 'driving' | 'transit';
  /** Map overlay: start collapsed to reduce visual noise; tap to expand full summary. */
  enableMapCollapse?: boolean;
};

const WEATHER_ICONS: Record<DayWeather['condition'], string> = {
  sunny: 'sunny-outline',
  cloudy: 'cloudy-outline',
  rainy: 'rainy-outline',
  snowy: 'snow-outline',
  partly_cloudy: 'partly-sunny-outline',
  stormy: 'thunderstorm-outline',
  windy: 'flag-outline',
};

const formatDistanceLabel = (meters: number) =>
  meters < 1000 ? `${Math.round(meters)} m` : `${(meters / 1000).toFixed(1)} km`;

const formatMinutes = (minutes: number): string => {
  if (!Number.isFinite(minutes) || minutes <= 0) return '0m';
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const getStartMs = (item: ItineraryItem) => {
  const base = item.startDate ? new Date(item.startDate) : new Date();
  const startTime = (item.startTime ?? '').trim();
  if (startTime && startTime !== 'flexible' && /^\d{1,2}:\d{2}/.test(startTime)) {
    const [h, m] = startTime.split(':').map((v) => parseInt(v, 10));
    if (!Number.isNaN(h)) {
      base.setHours(h, Number.isFinite(m) ? m : 0, 0, 0);
    }
  } else {
    base.setHours(0, 0, 0, 0);
  }
  return base.getTime();
};

export const DaySummaryCard: React.FC<DaySummaryCardProps> = ({
  day,
  weather,
  weatherByDateKey = {},
  weatherStatus = 'idle',
  weatherError = null,
  onRetryWeather,
  activities,
  pins = [],
  onTapNextActivity,
  onTapAddActivity,
  onTapViewAll,
  totalDays,
  compact = false,
  footer,
  preferredTravelMode = 'driving',
  enableMapCollapse = false,
}) => {
  const theme = useTheme();
  const [mapExpanded, setMapExpanded] = useState(false);
  const showWeatherShell = !!weather || weatherStatus === 'loading' || weatherStatus === 'error';
  
  // Sort activities by start time
  const sortedActivities = useMemo(() => {
    return [...activities].sort((a, b) => getStartMs(a) - getStartMs(b));
  }, [activities]);
  
  // Calculate metrics
  const metrics = useMemo(() => {
    if (sortedActivities.length < 2 || pins.length < 2) {
      return {
        totalDistance: 0,
        walkableLegs: 0,
        totalLegs: 0,
        longestLeg: null as { from: string; to: string; distance: number } | null,
      };
    }
    
    // Build pin lookup
    const pinLookup = new Map<string, { latitude: number; longitude: number }>();
    pins.forEach(pin => {
      pinLookup.set(pin.id, pin.coordinates);
      // Also map by itinerary item ID (pins often have id format "itinerary-{itemId}")
      const match = pin.id.match(/^itinerary-(.+)$/);
      if (match) {
        pinLookup.set(match[1], pin.coordinates);
      }
    });
    
    const legs: Array<{ from: string; to: string; distance: number }> = [];
    const OUTLIER_METERS = 300000; // guardrail: ignore >300km legs inside a day
    
    for (let i = 0; i < sortedActivities.length - 1; i++) {
      const current = sortedActivities[i];
      const next = sortedActivities[i + 1];
      
      const currentCoords = pinLookup.get(current.id) || pinLookup.get(`itinerary-${current.id}`);
      const nextCoords = pinLookup.get(next.id) || pinLookup.get(`itinerary-${next.id}`);
      
      if (currentCoords && nextCoords) {
        const distance = calculateDistance(currentCoords, nextCoords);
        if (distance > 0 && distance < OUTLIER_METERS) {
          legs.push({
            from: current.title,
            to: next.title,
            distance,
          });
        }
      }
    }
    
    const totalDistance = legs.reduce((sum, leg) => sum + leg.distance, 0);
    const WALKABLE_METERS = 1200; // align with other map summary components
    const walkableLegs = legs.filter((leg) => leg.distance <= WALKABLE_METERS).length;
    const longestLeg = legs.length > 0
      ? legs.reduce((longest, leg) => leg.distance > (longest?.distance ?? 0) ? leg : longest, legs[0])
      : null;
    
    return { totalDistance, walkableLegs, totalLegs: legs.length, longestLeg };
  }, [sortedActivities, pins]);
  
  // Find next upcoming activity
  const nextActivity = useMemo(() => {
    if (sortedActivities.length === 0) return null;
    
    const now = Date.now();
    // Find first activity that hasn't started yet
    const upcoming = sortedActivities.find(activity => getStartMs(activity) > now);
    // If all have passed, return the first one
    return upcoming || sortedActivities[0];
  }, [sortedActivities]);
  
  // Empty day state
  if (!day) {
    return null;
  }
  
  const hasActivities = activities.length > 0;
  const isAllDaysView = day.dayNumber === -1;

  const itineraryPinsCount = useMemo(() => {
    const ids = new Set<string>();
    pins.forEach((pin) => {
      const pinLooksLikeItinerary = pin.type ? pin.type === 'itinerary' : pin.id.startsWith('itinerary-');
      if (!pinLooksLikeItinerary) return;
      const normalizedId = pin.id.startsWith('itinerary-') ? pin.id.slice('itinerary-'.length) : pin.id;
      if (normalizedId) ids.add(normalizedId);
    });
    return ids.size;
  }, [pins]);

  const uniquePinSpots = useMemo(() => {
    const set = new Set<string>();
    pins.forEach((p) => {
      const pinLooksLikeItinerary = p.type ? p.type === 'itinerary' : p.id.startsWith('itinerary-');
      if (!pinLooksLikeItinerary) return;
      const c = p.coordinates;
      set.add(`${c.latitude.toFixed(5)}:${c.longitude.toFixed(5)}`);
    });
    return set.size;
  }, [pins]);

  const overlappingItineraryPinsCount = useMemo(() => {
    return Math.max(0, itineraryPinsCount - uniquePinSpots);
  }, [itineraryPinsCount, uniquePinSpots]);

  const hasOverlappingPins = overlappingItineraryPinsCount > 0;

  const walkabilityLabel = useMemo(() => {
    if (!hasActivities || metrics.totalLegs === 0) return '—';
    const ratio = metrics.walkableLegs / metrics.totalLegs;
    if (ratio >= 0.7) return 'High';
    if (ratio >= 0.4) return 'Medium';
    return 'Low';
  }, [hasActivities, metrics.totalLegs, metrics.walkableLegs]);

  const walkabilityRatio = useMemo(() => {
    if (!hasActivities || metrics.totalLegs === 0) return 0;
    return Math.max(0, Math.min(1, metrics.walkableLegs / metrics.totalLegs));
  }, [hasActivities, metrics.totalLegs, metrics.walkableLegs]);

  const walkabilityColor = useMemo(() => {
    if (walkabilityLabel === 'High') return theme.colors.success;
    if (walkabilityLabel === 'Medium') return theme.colors.warning || theme.colors.primary;
    return theme.colors.textSecondary;
  }, [theme.colors.primary, theme.colors.success, theme.colors.textSecondary, theme.colors.warning, walkabilityLabel]);

  const totalKmLabel = useMemo(() => {
    if (!metrics.totalDistance) return '0.0';
    return (metrics.totalDistance / 1000).toFixed(1);
  }, [metrics.totalDistance]);

  const estimatedTravelMinutes = useMemo(() => {
    if (!hasActivities || metrics.totalLegs === 0 || !metrics.totalDistance) return 0;
    const distKm = metrics.totalDistance / 1000;
    // Tuned to feel realistic for city travel (not highway speeds).
    const speedKmh = preferredTravelMode === 'walking' ? 4.8 : preferredTravelMode === 'transit' ? 16 : 22;
    // Per-leg overhead accounts for wait/parking/transfers/exit time.
    const overheadMinPerLeg = preferredTravelMode === 'walking' ? 2 : preferredTravelMode === 'transit' ? 8 : 7;
    const minutes = (distKm / speedKmh) * 60 + overheadMinPerLeg * metrics.totalLegs;
    return Math.max(0, Math.min(24 * 60, Math.round(minutes)));
  }, [hasActivities, metrics.totalDistance, metrics.totalLegs, preferredTravelMode]);

  const compactPinsLabel = useMemo(() => {
    if (!hasActivities) return 'No activities yet';
    const visibleLabel = `Pins: ${Math.min(itineraryPinsCount, activities.length)}/${activities.length}`;
    if (!hasOverlappingPins) return visibleLabel;
    return `${visibleLabel} • ${overlappingItineraryPinsCount} overlap`;
  }, [activities.length, hasActivities, hasOverlappingPins, itineraryPinsCount, overlappingItineraryPinsCount]);

  // "All Days" summary view memos MUST run unconditionally (hooks cannot be conditional).
  const tripTotals = useMemo(() => {
    if (!isAllDaysView) return { totalDistance: 0, totalLegs: 0 };
    if (!activities.length || pins.length < 2) {
      return { totalDistance: 0, totalLegs: 0 };
    }

    const pinLookup = new Map<string, { latitude: number; longitude: number }>();
    pins.forEach((pin) => {
      pinLookup.set(pin.id, pin.coordinates);
      const match = pin.id.match(/^itinerary-(.+)$/);
      if (match) pinLookup.set(match[1], pin.coordinates);
    });

    const byDay: Record<string, ItineraryItem[]> = {};
    activities.forEach((it) => {
      const key = toLocalDateKey(it.startDate);
      if (!key) return;
      if (!byDay[key]) byDay[key] = [];
      byDay[key].push(it);
    });
    Object.values(byDay).forEach((arr) => arr.sort((a, b) => getStartMs(a) - getStartMs(b)));

    let totalDistance = 0;
    let totalLegs = 0;
    const OUTLIER_METERS = 300000;
    for (const dayItems of Object.values(byDay)) {
      for (let i = 0; i < dayItems.length - 1; i++) {
        const a = dayItems[i];
        const b = dayItems[i + 1];
        const aC = pinLookup.get(a.id) || pinLookup.get(`itinerary-${a.id}`);
        const bC = pinLookup.get(b.id) || pinLookup.get(`itinerary-${b.id}`);
        if (!aC || !bC) continue;
        const d = calculateDistance(aC, bC);
        if (d > 0 && d < OUTLIER_METERS) {
          totalDistance += d;
          totalLegs += 1;
        }
      }
    }
    return { totalDistance, totalLegs };
  }, [activities, isAllDaysView, pins]);

  const tripTravelMinutes = useMemo(() => {
    if (!isAllDaysView) return 0;
    if (!tripTotals.totalDistance || tripTotals.totalLegs === 0) return 0;
    const distKm = tripTotals.totalDistance / 1000;
    const speedKmh = preferredTravelMode === 'walking' ? 4.8 : preferredTravelMode === 'transit' ? 16 : 22;
    const overheadMinPerLeg = preferredTravelMode === 'walking' ? 2 : preferredTravelMode === 'transit' ? 8 : 7;
    const minutes = (distKm / speedKmh) * 60 + overheadMinPerLeg * tripTotals.totalLegs;
    return Math.max(0, Math.min(24 * 60 * 30, Math.round(minutes)));
  }, [isAllDaysView, preferredTravelMode, tripTotals.totalDistance, tripTotals.totalLegs]);

  const weatherRangeLabel = useMemo(() => {
    if (!isAllDaysView) return '—';
    const days = Object.values(weatherByDateKey || {});
    if (!days.length) return '—';
    let minLow = Number.POSITIVE_INFINITY;
    let maxHigh = Number.NEGATIVE_INFINITY;
    for (const d of days) {
      if (typeof d.tempLow === 'number') minLow = Math.min(minLow, d.tempLow);
      if (typeof d.tempHigh === 'number') maxHigh = Math.max(maxHigh, d.tempHigh);
    }
    if (!Number.isFinite(minLow) || !Number.isFinite(maxHigh)) return '—';
    return `${Math.round(minLow)}°–${Math.round(maxHigh)}°`;
  }, [isAllDaysView, weatherByDateKey]);

  const weatherMetricValue = useMemo(() => {
    if (!isAllDaysView) return '—';
    if (weatherStatus === 'loading') return '…';
    if (weatherStatus === 'error') return '—';
    return weatherRangeLabel;
  }, [isAllDaysView, weatherRangeLabel, weatherStatus]);
  
  // "All Days" summary view: Trip at a glance
  if (isAllDaysView) {
    const cardSurface = {
      backgroundColor: theme.colorScheme === 'dark'
        ? theme.colors.surface + 'B3'
        : theme.colors.surface + 'C8',
    };

    if (enableMapCollapse && !mapExpanded) {
      return (
        <Card style={[styles.card, compact && styles.cardCompact, cardSurface]}>
          <Pressable
            onPress={() => setMapExpanded(true)}
            style={({ pressed }) => [styles.mapCollapseHeader, { opacity: pressed ? 0.88 : 1 }]}
            accessibilityRole="button"
            accessibilityLabel="Expand trip summary"
            accessibilityHint="Shows distances, travel time, weather, and next activity"
          >
            <View style={[styles.headerLeft, styles.mapCollapseHeaderMain]}>
              <Ionicons name="sparkles-outline" size={18} color={theme.colors.primary} />
              <View style={{ flex: 1, minWidth: 0 }}>
                <ThemedText variant="bodyStrong" numberOfLines={1} style={{ fontSize: 14 }}>
                  Trip at a glance
                </ThemedText>
                <Caption color={theme.colors.textSecondary} numberOfLines={1}>
                  {totalDays ? `${totalDays} days · ` : ''}
                  {activities.length} stops ·{' '}
                  {tripTotals.totalDistance ? (tripTotals.totalDistance / 1000).toFixed(1) : '0.0'} km
                  {weatherMetricValue && weatherMetricValue !== '—' ? ` · ${weatherMetricValue}` : ''}
                </Caption>
              </View>
            </View>
            <View style={styles.mapCollapseChevronWrap}>
              <Ionicons name="chevron-down" size={20} color={theme.colors.text} />
            </View>
          </Pressable>
          {footer ? <View style={styles.footer}>{footer}</View> : null}
        </Card>
      );
    }

    return (
      <Card
        style={[
          styles.card,
          compact && styles.cardCompact,
          cardSurface,
        ]}
      >
        <View style={styles.allDaysHeader}>
          <View style={styles.headerLeft}>
            <Ionicons name="sparkles-outline" size={18} color={theme.colors.primary} />
            <ThemedText variant="h3" style={styles.dayLabel}>
              Trip at a glance
            </ThemedText>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
            {totalDays ? (
              <Caption color={theme.colors.textSecondary}>
                {totalDays} day trip
              </Caption>
            ) : null}
            {weatherStatus === 'error' && onRetryWeather ? (
              <Pressable onPress={onRetryWeather} hitSlop={8}>
                <Ionicons name="refresh" size={18} color={theme.colors.textSecondary} />
              </Pressable>
            ) : null}
            {enableMapCollapse ? (
              <Pressable
                onPress={() => setMapExpanded(false)}
                hitSlop={10}
                accessibilityRole="button"
                accessibilityLabel="Collapse trip summary"
              >
                <Ionicons name="chevron-up" size={20} color={theme.colors.textSecondary} />
              </Pressable>
            ) : null}
          </View>
        </View>

        {/* Metrics grid: show everything at once (no horizontal scrolling) */}
        <View style={styles.tripAtGlanceChipsGrid}>
          {[
            { label: 'Stops', value: `${activities.length}` },
            {
              label: 'Distance',
              value: `${tripTotals.totalDistance ? (tripTotals.totalDistance / 1000).toFixed(1) : '0.0'} km`,
            },
            { label: 'Travel', value: formatMinutes(tripTravelMinutes) },
            { label: 'Weather', value: weatherMetricValue },
          ].map((m) => (
            <View
              key={m.label}
              style={[
                styles.metricChipGridItem,
                styles.metricChip,
                {
                  backgroundColor: theme.colors.background + '40',
                  borderColor: theme.colors.borderMuted,
                },
              ]}
            >
              <Caption color={theme.colors.textSecondary} numberOfLines={1} style={{ fontWeight: '700' }}>
                {m.label}
              </Caption>
              <ThemedText variant="bodyStrong" numberOfLines={1} style={{ fontSize: 14 }}>
                {m.value}
              </ThemedText>
            </View>
          ))}
        </View>

        {nextActivity ? (
          <Pressable
            onPress={() => onTapNextActivity?.(nextActivity)}
            style={({ pressed }) => [
              styles.tripNextRow,
              {
                backgroundColor: theme.colors.primary + '10',
                opacity: pressed ? 0.8 : 1,
              },
            ]}
          >
            <View style={{ flex: 1, gap: 2 }}>
              <Caption color={theme.colors.primary} style={{ fontWeight: '700' }}>
                Next up
              </Caption>
              <Body numberOfLines={1} style={{ fontWeight: '600' }}>
                {nextActivity.title}
              </Body>
              <Caption color={theme.colors.textSecondary} numberOfLines={1}>
                {nextActivity.startTime && nextActivity.startTime !== 'flexible' ? nextActivity.startTime : 'Flexible'}
                {nextActivity.location ? ` • ${nextActivity.location}` : ''}
              </Caption>
            </View>
            <Ionicons name="chevron-forward" size={18} color={theme.colors.textSecondary} />
          </Pressable>
        ) : null}

        <View style={styles.tripCtaRow}>
          <Ionicons name="information-circle-outline" size={14} color={theme.colors.textSecondary} />
          <Caption color={theme.colors.textSecondary} style={{ flex: 1 }}>
            Select a day to see the suggested order
          </Caption>
        </View>
        {footer ? <View style={styles.footer}>{footer}</View> : null}
      </Card>
    );
  }

  const dayCardSurface = {
    backgroundColor: theme.colorScheme === 'dark'
      ? theme.colors.surface + 'B3'
      : theme.colors.surface + 'C8',
  };

  if (enableMapCollapse && compact && !mapExpanded) {
    return (
      <Card style={[styles.card, dayCardSurface]}>
        <Pressable
          onPress={() => setMapExpanded(true)}
          style={({ pressed }) => [styles.mapCollapseHeader, { opacity: pressed ? 0.88 : 1 }]}
          accessibilityRole="button"
          accessibilityLabel="Expand day summary"
          accessibilityHint="Shows walkability, travel estimates, and activity list"
        >
          <View style={{ flex: 1, gap: 2, minWidth: 0 }}>
            {day.cityName ? (
              <Caption color={theme.colors.primary} style={{ fontWeight: '700' }} numberOfLines={1}>
                {day.cityName}
              </Caption>
            ) : null}
            {hasActivities ? (
              <>
                <Caption
                  color={
                    itineraryPinsCount < activities.length || hasOverlappingPins
                      ? (theme.colors.warning || theme.colors.error)
                      : theme.colors.textSecondary
                  }
                  style={{ fontWeight: '700' }}
                  numberOfLines={1}
                  ellipsizeMode="tail"
                >
                  {compactPinsLabel}
                </Caption>
                {hasOverlappingPins ? (
                  <Caption color={theme.colors.textSecondary} numberOfLines={2} style={styles.pinHintText}>
                    Zoom in to separate overlapping pins
                  </Caption>
                ) : null}
              </>
            ) : (
              <Caption color={theme.colors.textSecondary} style={{ fontWeight: '700' }} numberOfLines={1}>
                {compactPinsLabel}
              </Caption>
            )}
          </View>
          <View style={styles.mapCollapseTrailing}>
            <View style={styles.mapCollapseChevronWrap}>
              <Ionicons name="chevron-down" size={20} color={theme.colors.text} />
            </View>
            {showWeatherShell ? (
              <View style={[styles.weatherContainer, styles.weatherInMapCollapse]}>
                <Ionicons
                  name={(weather ? WEATHER_ICONS[weather.condition] : weatherStatus === 'error' ? 'alert-circle-outline' : 'cloud-outline') as any}
                  size={22}
                  color={theme.colors.primary}
                />
                <View style={styles.weatherTemp}>
                  {weather ? (
                    <>
                      <Caption style={{ fontWeight: '600' }}>{weather.tempHigh}°</Caption>
                      <Caption color={theme.colors.textSecondary}>{weather.tempLow}°</Caption>
                    </>
                  ) : weatherStatus === 'loading' ? (
                    <Caption style={{ fontWeight: '700' }}>…</Caption>
                  ) : (
                    <Caption style={{ fontWeight: '700' }}>—</Caption>
                  )}
                </View>
              </View>
            ) : null}
          </View>
        </Pressable>
        {footer ? <View style={styles.footer}>{footer}</View> : null}
      </Card>
    );
  }

  return (
    <Card
      style={[
        styles.card,
        dayCardSurface,
      ]}
    >
      {/* Header Row: Pins + (optional) Weather. Day/date are in the day picker, so avoid repetition here. */}
      <View style={styles.headerRow}>
        <View style={{ flex: 1, gap: 2 }}>
          {day.cityName ? (
            <Caption color={theme.colors.primary} style={{ fontWeight: '700' }}>
              {day.cityName}
            </Caption>
          ) : null}
          {hasActivities ? (
            <Caption
              color={
                itineraryPinsCount < activities.length || hasOverlappingPins
                  ? (theme.colors.warning || theme.colors.error)
                  : theme.colors.textSecondary
              }
              style={{ fontWeight: '700' }}
              numberOfLines={compact ? 1 : 2}
              ellipsizeMode="tail"
            >
              {compact
                ? compactPinsLabel
                : `${day?.dateLabel ? `${day.dateLabel} • ` : ''}Pins: ${Math.min(itineraryPinsCount, activities.length)}/${activities.length}${hasOverlappingPins ? ` (${overlappingItineraryPinsCount} overlap)` : ''}`}
            </Caption>
          ) : (
            <Caption
              color={theme.colors.textSecondary}
              style={{ fontWeight: '700' }}
              numberOfLines={compact ? 1 : 2}
              ellipsizeMode="tail"
            >
              {compact ? compactPinsLabel : `${day?.dateLabel ? `${day.dateLabel} • ` : ''}No activities yet`}
            </Caption>
          )}
          {hasActivities && hasOverlappingPins ? (
            <Caption color={theme.colors.textSecondary} numberOfLines={2} style={styles.pinHintText}>
              Zoom in to separate overlapping pins
            </Caption>
          ) : null}
        </View>

        {showWeatherShell && (
          <View style={styles.weatherContainer}>
            <Ionicons
              name={(weather ? WEATHER_ICONS[weather.condition] : weatherStatus === 'error' ? 'alert-circle-outline' : 'cloud-outline') as any}
              size={22}
              color={theme.colors.primary}
            />
            <View style={styles.weatherTemp}>
              {weather ? (
                <>
                  <Caption style={{ fontWeight: '600' }}>
                    {weather.tempHigh}°
                  </Caption>
                  <Caption color={theme.colors.textSecondary}>
                    {weather.tempLow}°
                  </Caption>
                </>
              ) : weatherStatus === 'loading' ? (
                <Caption style={{ fontWeight: '700' }}>…</Caption>
              ) : (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                  <Caption style={{ fontWeight: '700' }}>!</Caption>
                  {onRetryWeather ? (
                    <Pressable onPress={onRetryWeather} hitSlop={8}>
                      <Ionicons name="refresh" size={16} color={theme.colors.textSecondary} />
                    </Pressable>
                  ) : null}
                </View>
              )}
            </View>
          </View>
        )}
        {enableMapCollapse && compact ? (
          <Pressable
            onPress={() => setMapExpanded(false)}
            hitSlop={10}
            accessibilityRole="button"
            accessibilityLabel="Collapse day summary"
          >
            <Ionicons name="chevron-up" size={20} color={theme.colors.textSecondary} />
          </Pressable>
        ) : null}
      </View>
      
      {/* Empty Day State */}
      {!hasActivities && (
        <View style={[styles.emptyState, compact && styles.emptyStateCompact]}>
          <View style={styles.emptyContent}>
            <Ionicons
              name="calendar-outline"
              size={compact ? 22 : 32}
              color={theme.colors.textSecondary}
            />
            <View style={styles.emptyText}>
              <Body
                color={theme.colors.textSecondary}
                numberOfLines={compact ? 1 : 2}
                ellipsizeMode="tail"
              >
                No activities planned
              </Body>
              {!compact && (
                <Caption color={theme.colors.textSecondary} numberOfLines={2} ellipsizeMode="tail">
                  Add your first activity for this day
                </Caption>
              )}
            </View>
          </View>
          {onTapAddActivity && (
            <Button
              label={compact ? 'Add' : 'Add Activity'}
              variant="outlined"
              size="small"
              onPress={onTapAddActivity}
              icon={<Ionicons name="add" size={16} color={theme.colors.primary} />}
            />
          )}
        </View>
      )}
      
      {/* Metrics Row (primary content) */}
      {hasActivities && !compact && (
        <View style={styles.metricsRow}>
          <View style={styles.metric}>
            <ThemedText variant="h3" style={styles.metricValue}>
              {totalKmLabel}
            </ThemedText>
            <Caption color={theme.colors.textSecondary}>km travel</Caption>
          </View>

          <View style={styles.metricDivider} />

          <View style={styles.metric}>
            <ThemedText variant="h3" style={styles.metricValue}>
              {activities.length}
            </ThemedText>
            <Caption color={theme.colors.textSecondary}>Stops</Caption>
          </View>

          <View style={styles.metricDivider} />

          <View style={styles.metric}>
            <View style={styles.metricWithIcon}>
              <Ionicons
                name="walk"
                size={14}
                color={walkabilityColor}
              />
              <ThemedText variant="h3" style={styles.metricValue}>
                {walkabilityLabel}
              </ThemedText>
            </View>
            <Caption color={theme.colors.textSecondary}>
              Walkability{metrics.totalLegs ? ` • ${metrics.walkableLegs}/${metrics.totalLegs}` : ''}
            </Caption>
            <View style={styles.walkabilityTrack}>
              <View style={[styles.walkabilityFill, { width: `${Math.round(walkabilityRatio * 100)}%`, backgroundColor: walkabilityColor }]} />
            </View>
          </View>
        </View>
      )}
      
      {/* Compact metrics for smaller card */}
      {hasActivities && compact && (
        <View style={styles.compactMetrics}>
          <Caption
            color={theme.colors.textSecondary}
            style={styles.compactMetricsText}
            numberOfLines={2}
            ellipsizeMode="tail"
          >
            {totalKmLabel} km • {activities.length} stops • Travel ~{formatMinutes(estimatedTravelMinutes)} • Walkability {walkabilityLabel}
          </Caption>
        </View>
      )}
      
      {/* Next Activity Preview */}
      {hasActivities && nextActivity && !compact && (
        <Pressable
          onPress={() => onTapNextActivity?.(nextActivity)}
          style={({ pressed }) => [
            styles.nextActivityContainer,
            {
              backgroundColor: theme.colors.primary + '10',
              opacity: pressed ? 0.8 : 1,
            },
          ]}
        >
          <View style={styles.nextActivityContent}>
            <Caption color={theme.colors.primary} style={{ fontWeight: '600' }}>
              {day.isToday ? 'Next up' : 'First'}
            </Caption>
            <Body numberOfLines={1} style={{ fontWeight: '500' }}>
              {nextActivity.title}
            </Body>
            {nextActivity.startTime && nextActivity.startTime !== 'flexible' && (
              <Caption color={theme.colors.textSecondary}>
                {nextActivity.startTime}
                {nextActivity.location && ` • ${nextActivity.location}`}
              </Caption>
            )}
          </View>
          <Ionicons
            name="navigate-outline"
            size={20}
            color={theme.colors.primary}
          />
        </Pressable>
      )}
      
      {/* Longest leg info */}
      {hasActivities && metrics.longestLeg && metrics.longestLeg.distance > 2000 && !compact && (
        <Caption color={theme.colors.textSecondary} style={styles.longestLegText}>
          Longest: {formatDistanceLabel(metrics.longestLeg.distance)} from {metrics.longestLeg.from} → {metrics.longestLeg.to}
        </Caption>
      )}
      
      {/* View all button for compact mode */}
      {hasActivities && compact && onTapViewAll && (
        <Pressable
          onPress={onTapViewAll}
          style={({ pressed }) => [
            styles.viewAllButton,
            { opacity: pressed ? 0.7 : 1 },
          ]}
        >
          <Caption color={theme.colors.primary} style={{ fontWeight: '600' }}>
            View details
          </Caption>
          <Ionicons name="chevron-forward" size={14} color={theme.colors.primary} />
        </Pressable>
      )}

      {footer ? <View style={styles.footer}>{footer}</View> : null}
    </Card>
  );
};

const styles = StyleSheet.create({
  card: {
    padding: 10,
    borderRadius: 14,
    gap: 8,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: 8,
      },
      android: {
        elevation: 3,
      },
    }),
  },
  cardCompact: {
    padding: 8,
    gap: 6,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  dayLabel: {
    fontWeight: '700',
    fontSize: 15,
  },
  weatherContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 12,
    backgroundColor: 'rgba(0,0,0,0.04)',
  },
  weatherTemp: {
    alignItems: 'flex-end',
  },
  metricsRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  metric: {
    alignItems: 'flex-start',
    gap: 2,
  },
  metricValue: {
    fontWeight: '700',
    fontSize: 16,
  },
  metricWithIcon: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  metricDivider: {
    width: 1,
    height: 24,
    backgroundColor: 'rgba(0,0,0,0.1)',
    alignSelf: 'center',
  },
  tripAtGlanceChips: {
    flexDirection: 'row',
    gap: 8,
    paddingTop: 2,
    paddingBottom: 2,
  },
  tripAtGlanceChipsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  metricChip: {
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 12,
    borderWidth: 1,
    minWidth: 92,
    gap: 2,
  },
  metricChipGridItem: {
    width: '48%',
  },
  tripNextRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 10,
    borderRadius: 12,
    gap: 10,
  },
  tripCtaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 2,
  },
  walkabilityTrack: {
    width: 72,
    height: 4,
    borderRadius: 999,
    backgroundColor: 'rgba(0,0,0,0.08)',
    overflow: 'hidden',
    marginTop: 2,
  },
  walkabilityFill: {
    height: '100%',
    borderRadius: 999,
  },
  compactMetrics: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  compactMetricsText: {
    flexShrink: 1,
  },
  nextActivityContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 10,
    borderRadius: 12,
    gap: 10,
  },
  nextActivityContent: {
    flex: 1,
    gap: 2,
  },
  longestLegText: {
    marginTop: -4,
  },
  emptyState: {
    alignItems: 'center',
    gap: 12,
    paddingVertical: 8,
  },
  emptyStateCompact: {
    gap: 8,
    paddingVertical: 2,
    alignItems: 'flex-start',
  },
  emptyContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  emptyText: {
    gap: 2,
  },
  footer: {
    marginTop: 6,
  },
  viewAllButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingTop: 4,
  },
  allDaysHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  mapCollapseHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  mapCollapseHeaderMain: {
    flex: 1,
    minWidth: 0,
  },
  mapCollapseChevronWrap: {
    flexShrink: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  mapCollapseTrailing: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  weatherInMapCollapse: {
    flexShrink: 1,
    minWidth: 0,
  },
  pinHintText: {
    marginTop: 2,
    lineHeight: 16,
  },
});

export default DaySummaryCard;
